import { type ReactNode } from 'react';
import { Trash2 } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import SaveToRepoButton from './SaveToRepoButton';

const MODELS = ['', 'sonnet', 'opus', 'haiku', 'inherit'];
const EFFORT_LEVELS = ['', 'low', 'medium', 'high', 'xhigh', 'max'];

interface FrontmatterEditorFieldsProps {
  attrs: Record<string, unknown>;
  body: string;
  updateAttr: (key: string, value: unknown) => void;
  updateBody: (body: string) => void;
  loading: boolean;
  saving: boolean;
  saved: boolean;
  onSave: () => void;
  onDelete: () => void;
  extraFields?: ReactNode;
  emptyMessage: string;
  hasItems: boolean;
  isSelected: boolean;
}

export function FrontmatterEditorFields({
  attrs,
  body,
  updateAttr,
  updateBody,
  loading,
  saving,
  saved,
  onSave,
  onDelete,
  extraFields,
  emptyMessage,
  hasItems,
  isSelected,
}: FrontmatterEditorFieldsProps) {
  if (!isSelected) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-slate-400 py-8">
        {hasItems ? `Select to edit` : emptyMessage}
      </div>
    );
  }

  if (loading) {
    return <LoadingState size="md" className="flex-1" />;
  }

  return (
    <div className="flex-1 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Name</label>
          <input
            type="text"
            value={(attrs.name as string) || ''}
            onChange={(e) => updateAttr('name', e.target.value)}
            className="w-full px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-mono text-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Description</label>
          <input
            type="text"
            value={(attrs.description as string) || ''}
            onChange={(e) => updateAttr('description', e.target.value)}
            className="w-full px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Model</label>
          <select
            value={(attrs.model as string) || ''}
            onChange={(e) => updateAttr('model', e.target.value || undefined)}
            className="w-full px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
          >
            {MODELS.map((m) => (
              <option key={m} value={m}>{m || '(inherit)'}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Effort</label>
          <select
            value={(attrs.effort as string) || ''}
            onChange={(e) => updateAttr('effort', e.target.value || undefined)}
            className="w-full px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100"
          >
            {EFFORT_LEVELS.map((l) => (
              <option key={l} value={l}>{l || '(inherit)'}</option>
            ))}
          </select>
        </div>
      </div>

      {extraFields}

      <div>
        <label className="block text-xs text-slate-500 mb-1">Instructions (markdown)</label>
        <textarea
          value={body}
          onChange={(e) => updateBody(e.target.value)}
          rows={8}
          className="w-full p-3 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono text-slate-900 dark:text-slate-100 resize-y"
        />
      </div>

      <SaveToRepoButton saving={saving} saved={saved} onSave={onSave} />
      <button
        onClick={onDelete}
        className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
      >
        <Trash2 className="w-3 h-3" /> Remove
      </button>
    </div>
  );
}
